import { Formik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import callApi from '../../../../utils/callApi';
import { config, getAssetUrl } from '../../../../utils/config';
import { reportErrorToSentry } from '../../../../utils/initializeSentry';
import Button from '../../../atoms/Button';
import MainHeadline from '../../../atoms/MainHeadline';
import EmailField from '../../../fields/EmailField';
import { showDefaultError, showNotification } from '../../Notifications';
import './style.scss';

type ForgotPasswordProps = {
  initialEmail?: string;
  onClose: () => void;
};

const ForgotPassword = ({ onClose, initialEmail }: ForgotPasswordProps) => {
  const { formatMessage } = useIntl();

  const requestPasswordReset = ({ email }: { email: string }) =>
    callApi({
      endpoint: '/customer/password/reset',
      payload: { email, resetUrl: config.urls.passwordResetConfirmation },
      flow: 'legacy',
    });

  const onSubmit = async ({ email }: { email: string }) => {
    try {
      await requestPasswordReset({ email });
      showNotification({
        level: 'success',
        text: formatMessage({ id: 'auth.notifications.requestPasswordResetSuccess' }),
        closable: true,
        autoClose: true,
      });
      onClose();
    } catch (e) {
      console.error(e);
      showDefaultError();
      reportErrorToSentry(e);
    }
  };

  const testIdPrefix = 'login-forgot-password';

  return (
    <Formik initialValues={{ email: initialEmail ?? '' }} onSubmit={onSubmit}>
      {({ submitForm, isSubmitting }) => (
        <div className="forgot-password">
          <div className="forgot-password__headline">
            <MainHeadline>
              <FormattedMessage id="auth.forgotPassword.headline" />
            </MainHeadline>
            <img className="forgot-password__close" src={getAssetUrl('common/remove.svg')} onClick={onClose} />
          </div>
          <div className="forgot-password__sub-text">
            <FormattedMessage id="auth.forgotPassword.subText" />
          </div>
          <div className="form__field-container">
            <EmailField testIdPrefix={testIdPrefix} type="passwordReset" />
          </div>
          <div className="form__row forgot-password__action-buttons">
            <div className="form__field-container">
              <Button
                type="button"
                className="auth-form__back"
                onClick={() => onClose()}
                data-test-id="login-forgot-password-back"
              >
                <FormattedMessage id="generic.back" />
              </Button>
            </div>
            <div className="form__field-container">
              <Button
                type="button"
                className="auth-form__send"
                onClick={() => submitForm()}
                disabled={isSubmitting}
                isLoading={isSubmitting}
                data-test-id="login-forgot-password-submit"
              >
                <FormattedMessage id="generic.send" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
};

export default ForgotPassword;
